// import "../styles/tailwind.css";
import "../styles/style.scss";

/* Library ================ */
// Swiper
import Swiper from "swiper/bundle";
import "swiper/css/bundle";

// PhotoSwipe
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";

// GSAP
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

/* Utilities ================ */
const assetRoot = document.documentElement.dataset.root || './';
const mqSp = window.matchMedia("(max-width: 768px)");

const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`画像を読み込めませんでした: ${src}`));
    img.src = src;
  });
};

const setPhotoSwipeImageSize = async (gallery) => {
  const links = [...gallery.querySelectorAll(".p-lower__kv__item__link")];

  if (!links.length) return;

  await Promise.allSettled(
    links.map(async (link) => {
      const imgEl = link.querySelector("img");

      // build後にhrefとimg srcがズレる可能性があるので、
      // 実際に画面へ表示されているimgのURLを優先して使う
      const imageSrc = imgEl?.currentSrc || imgEl?.src || link.href;

      if (!imageSrc) return;

      const img = await loadImage(imageSrc);

      link.setAttribute("href", imageSrc);
      link.setAttribute("data-pswp-src", imageSrc);
      link.setAttribute("data-pswp-width", img.naturalWidth);
      link.setAttribute("data-pswp-height", img.naturalHeight);
      link.classList.remove("is-loading");
    }),
  );
};

/* Slider ================ */
const initTopKVSlider = () => {
  const topKVSlider = document.querySelector(".p-top__kv .swiper");

  if (!topKVSlider) return;

  new Swiper(topKVSlider, {
    loop: true,
    effect: "fade",
    fadeEffect: {
      crossFade: true,
    },
    speed: 3000,
    autoplay: {
      delay: 6000,
      waitForTransition: false,
    },
  });
};

let lowerKVSwiper = null;

const initLowerKVSwiper = () => {
  const swiperEl = document.querySelector(".js-lower-kv-swiper");

  if (!swiperEl) return;

  if (mqSp.matches) {
    if (!lowerKVSwiper) {
      lowerKVSwiper = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 24,
        loop: false,

        allowTouchMove: true,
        simulateTouch: true,
        touchStartPreventDefault: true,

        pagination: {
          el: swiperEl.querySelector(".js-lower-kv-swiper-pagination"),
          clickable: true,
        },

        navigation: {
          nextEl: swiperEl.querySelector(".js-lower-kv-swiper-next"),
          prevEl: swiperEl.querySelector(".js-lower-kv-swiper-prev"),
        },

        observer: true,
        observeParents: true,

        // PhotoSwipe用のクリックをSwiperが邪魔しないようにする
        preventClicks: false,
        preventClicksPropagation: false,
      });
    } else {
      lowerKVSwiper.update();
    }

    return;
  }

  if (lowerKVSwiper) {
    lowerKVSwiper.destroy(true, true);
    lowerKVSwiper = null;
  }
};

/* PhotoSwipe ================ */
const initPhotoSwipeGallery = async (selector) => {
  const gallery = document.querySelector(selector);

  if (!gallery) return;

  await setPhotoSwipeImageSize(gallery);

  const lightbox = new PhotoSwipeLightbox({
    gallery,
    children: ".p-lower__kv__item__link",
    pswpModule: () => import("photoswipe"),
  });

  lightbox.init();

  const zoomButton = gallery.querySelector(".p-lower__kv__item__zoom");

  if (!zoomButton) return;

  zoomButton.setAttribute("role", "button");
  zoomButton.setAttribute("tabindex", "0");
  zoomButton.setAttribute("aria-label", "画像を拡大表示する");

  const openCurrentImage = () => {
    const links = [...gallery.querySelectorAll(".p-lower__kv__item__link")];

    if (!links.length) return;

    // SP Swiperの場合は現在表示中のslide内のリンクを開く
    const activeSlide = gallery.querySelector(".swiper-slide-active");
    const activeLink = activeSlide?.querySelector(".p-lower__kv__item__link");

    let index = activeLink ? links.indexOf(activeLink) : 0;

    if (index < 0) {
      index = 0;
    }

    lightbox.loadAndOpen(index);
  };

  zoomButton.addEventListener("click", openCurrentImage);

  zoomButton.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openCurrentImage();
    }
  });
};

const initLowerKVPhotoSwipe = async () => {
  await Promise.all([
    initPhotoSwipeGallery(".js-lower-kv-gallery-pc"),
    initPhotoSwipeGallery(".js-lower-kv-gallery-sp"),
  ]);
};

/* Animation ================ */
const initTopCatchphraseAnimation = () => {

  const topCatchphraseCircle = document.querySelector(".js-circle-mask-path");

  if (!topCatchphraseCircle) return;

  const length = topCatchphraseCircle.getTotalLength();

  gsap.set(topCatchphraseCircle, {
    strokeDasharray: length,
    strokeDashoffset: length,
  });

  gsap.to(topCatchphraseCircle, {
    strokeDashoffset: 0,
    delay: 0.6,
    duration: 1,
    ease: "power3.inOut",
    scrollTrigger: {
      trigger: ".p-top__catchphrase",
      start: "top 70%",
      once: true,
    },
  });
};


// top lineup items
const initTopLineupItemAnimation = () => {
  const items = document.querySelectorAll(".p-top__lineup__item");

  if (!items.length) return;

  items.forEach((item) => {
    const svgs = item.querySelectorAll("svg");

    if (!svgs.length) return;

    svgs.forEach((svg) => {
      const photo = svg.querySelector("#lineup__item__photo");
      const packageItem = svg.querySelector("#lineup__item__package");
      const label = svg.querySelector("#lineup__item__label");

      const descriptions = [
        svg.querySelector("#lineup__item__description01"),
        svg.querySelector("#lineup__item__description02"),
        svg.querySelector("#lineup__item__description03"),
      ].filter(Boolean);

      if (photo) {
        gsap.set(photo, {
          opacity: 0,
          x: 80,
        });
      }

      if (packageItem) {
        gsap.set(packageItem, {
          opacity: 0,
          scale: 0.86,
          transformOrigin: "50% 50%",
          transformBox: "fill-box",
        });
      }

      if (label) {
        gsap.set(label, {
          opacity: 0,
          y: -70,
        });
      }

      if (descriptions.length) {
        gsap.set(descriptions, {
          opacity: 0,
          filter: "blur(10px)",
        });
      }
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: item,
        start: mqSp.matches ? "top bottom-=100" : "top 70%",
        once: true,
      },
    });

    svgs.forEach((svg) => {
      const photo = svg.querySelector("#lineup__item__photo");
      const packageItem = svg.querySelector("#lineup__item__package");
      const label = svg.querySelector("#lineup__item__label");

      const descriptions = [
        svg.querySelector("#lineup__item__description01"),
        svg.querySelector("#lineup__item__description02"),
        svg.querySelector("#lineup__item__description03"),
      ].filter(Boolean);

      if (photo) {
        tl.to(
          photo, {
            opacity: 1,
            x: 0,
            duration: 0.65,
            ease: "power3.out",
          },
          mqSp.matches ? "-=1.2" : "",
        );
      }

      if (packageItem) {
        tl.to(
          packageItem,
          {
            opacity: 1,
            scale: 1,
            duration: 0.55,
            ease: "back.out(1.45)",
            transformOrigin: "50% 50%",
            transformBox: "fill-box",
          },
          "-=0.28",
        );
      }

      if (label) {
        tl.to(
          label,
          {
            opacity: 1,
            y: 0,
            duration: 0.55,
            ease: "power3.out",
          },
          "-=0.22",
        );
      }

      if (descriptions.length) {
        tl.to(
          descriptions,
          {
            opacity: 1,
            filter: "blur(0px)",
            duration: 0.6,
            ease: "sine.out",
            stagger: 0.18,
          },
          "+=0.6",
        );
      }
    });
  });
};


// top lineup noren
const initTopLineupNorenAnimation = () => {
  const noren = document.querySelector(".js-lineup-noren");

  if (!noren) return;

  const frames = noren.querySelectorAll(".p-top__lineup__noren__frame");

  if (!frames.length) return;

  gsap.set(frames, {
    opacity: 0,
  });

  gsap.set(frames[0], {
    opacity: 1,
  });

  const tl = gsap.timeline({
    repeat: -1,
    repeatDelay: 1.8,
    scrollTrigger: {
      trigger: noren,
      start: "top 80%",
      once: false,
    },
  });

  tl.set(frames, { opacity: 0 })
    .set(frames[0], { opacity: 1 })
    .to({}, { duration: 0.18 })
    .set(frames, { opacity: 0 })
    .set(frames[1], { opacity: 1 })
    .to({}, { duration: 0.16 })
    .set(frames, { opacity: 0 })
    .set(frames[2], { opacity: 1 })
    .to({}, { duration: 0.16 })
    .set(frames, { opacity: 0 })
    .set(frames[3], { opacity: 1 })
    .to({}, { duration: 0.22 })
    .set(frames, { opacity: 0 })
    .set(frames[2], { opacity: 1 })
    .to({}, { duration: 0.16 })
    .set(frames, { opacity: 0 })
    .set(frames[1], { opacity: 1 })
    .to({}, { duration: 0.16 })
    .set(frames, { opacity: 0 })
    .set(frames[0], { opacity: 1 })
    .to({}, { duration: 0.8 });
};


// top gallery title scroll
const initTopGalleryTitleAnimation = () => {
  const title = document.querySelector(".js-gallery-title");

  if (!title) return;

  const svg = title.querySelector("svg");

  if (!svg) return;

  const contents = svg.querySelector("#gallery-title-contents");
  const parts = svg.querySelector("#gallery-title-contents-parts");
  const line = svg.querySelector("#gallery-title-contents-line path");
  const lineGroup = svg.querySelector("#gallery-title-contents-line");
  const startParts = svg.querySelector("#gallery-title-contents-parts-start");
  const endParts = svg.querySelector("#gallery-title-contents-parts-end");

  if (!contents || !parts || !line) return;

  const svgNS = "http://www.w3.org/2000/svg";
  const viewBox = svg.viewBox.baseVal;
  const clipId = `gallery-title-contents-clip-${Math.random().toString(36).slice(2)}`;

  let defs = svg.querySelector("defs");

  if (!defs) {
    defs = document.createElementNS(svgNS, "defs");
    svg.insertBefore(defs, svg.firstChild);
  }

  const clipPath = document.createElementNS(svgNS, "clipPath");
  const clipRect = document.createElementNS(svgNS, "rect");

  clipPath.setAttribute("id", clipId);
  clipPath.setAttribute("clipPathUnits", "userSpaceOnUse");

  clipRect.setAttribute("x", viewBox.x);
  clipRect.setAttribute("y", viewBox.y);
  clipRect.setAttribute("width", 0);
  clipRect.setAttribute("height", viewBox.height);

  clipPath.appendChild(clipRect);
  defs.appendChild(clipPath);

  contents.setAttribute("clip-path", `url(#${clipId})`);

  // ガイド用・位置確認用パーツは非表示
  gsap.set([lineGroup, startParts, endParts], {
    autoAlpha: 0,
  });

  // 紙面はclipで隠す
  gsap.set(contents, {
    opacity: 1,
  });

  gsap.set(clipRect, {
    attr: {
      width: 0,
    },
  });

  // pathの向きが右→左なので、start: 1 から end: 0 に動かす
  gsap.set(parts, {
    opacity: 0,
    motionPath: {
      path: line,
      align: line,
      alignOrigin: [1.02, 1],
      start: 1,
      end: 1,
    },
  });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: title,
      start: "top 80%",
      once: true,
    },
  });

  tl.to(parts, {
    opacity: 1,
    duration: 0.18,
    ease: "sine.out",
  })
  .to(
    parts,
    {
      duration: 1.25,
      ease: "power2.inOut",
      motionPath: {
        path: line,
        align: line,
        alignOrigin: [1.02, 1],
        start: 1,
        end: 0,
      },
    },
    0,
  ).to(
    clipRect,
    {
      duration: 1.25,
      ease: "power2.inOut",
      attr: {
        width: viewBox.width,
      },
    },
    0,
  );
};


// top gallery movie slider
import thumb01 from '@/assets/images/top/gallery-movie-thumb01.webp';
import thumb02 from '@/assets/images/top/gallery-movie-thumb02.webp';
import thumb03 from '@/assets/images/top/gallery-movie-thumb03.webp';

const initTopGalleryMovieSlider = () => {
  const root = document.querySelector(".js-gallery-movie");

  if (!root) return;

  const svg = root.querySelector("svg");

  if (!svg) return;

  const frame = svg.querySelector("#gallery-movie-frame");
  const controller = svg.querySelector("#gallery-movie-controller");
  const screenClipPath = svg.querySelector("#gallery-movie-screen-clip-path");

  if (!frame || !controller || !screenClipPath) return;

  const slides = [
    {
      thumb: thumb01,
      url: "https://www.youtube.com/watch?v=T-NAz5gSLXY",
      label: "CM動画1を見る",
    },
    {
      thumb: thumb02,
    url: "https://www.youtube.com/watch?v=5ltDwBTVh4g",
      label: "CM動画2を見る",
    },
    {
      thumb: thumb03,
      url: "https://www.youtube.com/watch?v=0wdkDiJV8Bo",
      label: "CM動画3を見る",
    },
  ];

  const svgNS = "http://www.w3.org/2000/svg";
  const xlinkNS = "http://www.w3.org/1999/xlink";

  const screenClipShape = screenClipPath.querySelector("path");

  if (!screenClipShape) return;

  const bbox = screenClipShape.getBBox();
  const clipId = `gallery-movie-screen-clip-${Math.random().toString(36).slice(2)}`;

  let defs = svg.querySelector("defs");

  if (!defs) {
    defs = document.createElementNS(svgNS, "defs");
    svg.insertBefore(defs, svg.firstChild);
  }

  const clipPath = document.createElementNS(svgNS, "clipPath");
  clipPath.setAttribute("id", clipId);
  clipPath.setAttribute("clipPathUnits", "userSpaceOnUse");

  const clipShape = screenClipShape.cloneNode(true);

  clipShape.removeAttribute("id");
  clipShape.setAttribute("fill", "#fff");
  clipShape.setAttribute("stroke", "none");

  clipPath.appendChild(clipShape);
  defs.appendChild(clipPath);

  gsap.set(screenClipPath, {
    autoAlpha: 0,
  });

  const movieLayer = document.createElementNS(svgNS, "g");
  movieLayer.setAttribute("id", "gallery-movie-thumbnails");

  frame.parentNode.insertBefore(movieLayer, frame);

  const toYouTubeEmbedUrl = (url) => {
    try {
      const parsedUrl = new URL(url);
      let videoId = "";

      if (parsedUrl.hostname.includes("youtu.be")) {
        videoId = parsedUrl.pathname.replace("/", "");
      }

      if (parsedUrl.hostname.includes("youtube.com")) {
        if (parsedUrl.pathname.includes("/watch")) {
          videoId = parsedUrl.searchParams.get("v") || "";
        }

        if (parsedUrl.pathname.includes("/embed/")) {
          videoId = parsedUrl.pathname.split("/embed/")[1]?.split("/")[0] || "";
        }

        if (parsedUrl.pathname.includes("/shorts/")) {
          videoId = parsedUrl.pathname.split("/shorts/")[1]?.split("/")[0] || "";
        }
      }

      if (!videoId) return "";

      return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`;
    } catch {
      return "";
    }
  };

  const modal = document.createElement("div");
  modal.className = "p-top__gallery__modal js-gallery-movie-modal";
  modal.setAttribute("aria-hidden", "true");

  modal.innerHTML = `
    <div class="p-top__gallery__modal__overlay js-gallery-movie-modal-close"></div>
    <div class="p-top__gallery__modal__content" role="dialog" aria-modal="true" aria-label="CM動画">
      <button type="button" class="p-top__gallery__modal__close js-gallery-movie-modal-close" aria-label="動画を閉じる">
        <span></span>
        <span></span>
      </button>
      <div class="p-top__gallery__modal__movie">
        <iframe
          class="js-gallery-movie-iframe"
          src=""
          title="CM動画"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowfullscreen
        ></iframe>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const modalIframe = modal.querySelector(".js-gallery-movie-iframe");
  const modalCloseButtons = modal.querySelectorAll(".js-gallery-movie-modal-close");

  const openMovieModal = (slide) => {
    const embedUrl = toYouTubeEmbedUrl(slide.url);

    if (!embedUrl) {
      window.open(slide.url, "_blank", "noopener");
      return;
    }

    stopAutoSlide();

    modalIframe.src = embedUrl;
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("is-gallery-movie-modal-open");
  };

  const closeMovieModal = () => {
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("is-gallery-movie-modal-open");

    // iframeのsrcを空にして、閉じたら動画を止める
    modalIframe.src = "";

    startAutoSlide();
  };

  modalCloseButtons.forEach((button) => {
    button.addEventListener("click", closeMovieModal);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (!modal.classList.contains("is-open")) return;

    closeMovieModal();
  });

  const slideElements = slides.map((slide, index) => {
    const link = document.createElementNS(svgNS, "a");
    const image = document.createElementNS(svgNS, "image");

    link.setAttributeNS(xlinkNS, "href", slide.url);
    link.setAttribute("href", slide.url);
    link.setAttribute("aria-label", slide.label);

    image.setAttributeNS(xlinkNS, "href", slide.thumb);
    image.setAttribute("href", slide.thumb);
    image.setAttribute("x", bbox.x);
    image.setAttribute("y", bbox.y);
    image.setAttribute("width", bbox.width);
    image.setAttribute("height", bbox.height);
    image.setAttribute("preserveAspectRatio", "xMidYMid slice");
    image.setAttribute("clip-path", `url(#${clipId})`);

    link.appendChild(image);
    movieLayer.appendChild(link);

    link.addEventListener("click", (event) => {
      event.preventDefault();
      openMovieModal(slide);
    });

    link.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openMovieModal(slide);
      }
    });

    gsap.set(link, {
      opacity: index === 0 ? 1 : 0,
      pointerEvents: index === 0 ? "auto" : "none",
      cursor: "pointer",
    });

    return link;
  });

  const prevButton = svg.querySelector("#gallery-movie-prev");
  const nextButton = svg.querySelector("#gallery-movie-next");

  const bullets = [
    svg.querySelector("#gallery-movie-bullet01"),
    svg.querySelector("#gallery-movie-bullet02"),
    svg.querySelector("#gallery-movie-bullet03"),
  ].filter(Boolean);

  let currentIndex = 0;
  let isAnimating = false;
  let intervalId = null;

  const addSvgHitArea = (target) => {
    if (!target) return;

    if (target.querySelector(".js-svg-hit-area")) return;

    const targetBBox = target.getBBox();
    const hitArea = document.createElementNS(svgNS, "rect");
    const padding = 10;

    hitArea.setAttribute("x", targetBBox.x - padding);
    hitArea.setAttribute("y", targetBBox.y - padding);
    hitArea.setAttribute("width", targetBBox.width + padding * 2);
    hitArea.setAttribute("height", targetBBox.height + padding * 2);
    hitArea.setAttribute("fill", "transparent");
    hitArea.setAttribute("pointer-events", "all");
    hitArea.setAttribute("class", "js-svg-hit-area");

    target.insertBefore(hitArea, target.firstChild);
  };

  const updateBullets = () => {
    bullets.forEach((bullet, index) => {
      const isActive = index === currentIndex;
      const shapes = bullet.querySelectorAll("circle, ellipse, path");

      gsap.set(bullet, {
        opacity: isActive ? 1 : 0.75,
      });

      shapes.forEach((shape) => {
        if (shape.classList.contains("js-svg-hit-area")) return;

        shape.setAttribute("fill", isActive ? "#fff3b0" : "none");
        shape.setAttribute("stroke", "#fff3b0");
        shape.setAttribute("stroke-width", "1.5");
      });
    });
  };

  const setSvgButtonStyle = (button, label) => {
    if (!button) return;

    addSvgHitArea(button);

    button.style.cursor = "pointer";
    button.setAttribute("role", "button");
    button.setAttribute("tabindex", "0");

    if (label) {
      button.setAttribute("aria-label", label);
    }
  };

  setSvgButtonStyle(prevButton, "前のCM動画へ");
  setSvgButtonStyle(nextButton, "次のCM動画へ");

  bullets.forEach((bullet, index) => {
    setSvgButtonStyle(bullet, `CM動画${index + 1}へ切り替える`);
  });

  const pressController = () => {
    gsap.fromTo(
      controller,
      {
        y: 0,
        scaleY: 1,
      },
      {
        y: 9,
        scaleY: 0.94,
        duration: 0.12,
        ease: "power2.out",
        yoyo: true,
        repeat: 1,
        transformOrigin: "50% 100%",
        transformBox: "fill-box",
      },
    );
  };

  const goToSlide = (nextIndex) => {
    if (isAnimating || nextIndex === currentIndex) return;

    isAnimating = true;

    const current = slideElements[currentIndex];
    const next = slideElements[nextIndex];

    pressController();

    currentIndex = nextIndex;
    updateBullets();

    gsap
      .timeline({
        onComplete: () => {
          isAnimating = false;
        },
      })
      .set(next, {
        pointerEvents: "auto",
      })
      .to(current, {
        opacity: 0,
        duration: 0.25,
        ease: "sine.out",
        onComplete: () => {
          gsap.set(current, {
            pointerEvents: "none",
          });
        },
      })
      .fromTo(
        next,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.35,
          ease: "sine.out",
        },
        "-=0.05",
      );
  };

  const goToNextSlide = () => {
    const nextIndex = (currentIndex + 1) % slideElements.length;

    goToSlide(nextIndex);
  };

  const goToPrevSlide = () => {
    const prevIndex = (currentIndex - 1 + slideElements.length) % slideElements.length;

    goToSlide(prevIndex);
  };

  const stopAutoSlide = () => {
    if (!intervalId) return;

    window.clearInterval(intervalId);
    intervalId = null;
  };

  const startAutoSlide = () => {
    if (intervalId) return;

    intervalId = window.setInterval(goToNextSlide, 3500);
  };

  const restartAutoSlide = () => {
    stopAutoSlide();
    startAutoSlide();
  };

  if (prevButton) {
    prevButton.addEventListener("click", () => {
      goToPrevSlide();
      restartAutoSlide();
    });

    prevButton.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        goToPrevSlide();
        restartAutoSlide();
      }
    });
  }

  if (nextButton) {
    nextButton.addEventListener("click", () => {
      goToNextSlide();
      restartAutoSlide();
    });

    nextButton.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        goToNextSlide();
        restartAutoSlide();
      }
    });
  }

  bullets.forEach((bullet, index) => {
    bullet.addEventListener("click", () => {
      goToSlide(index);
      restartAutoSlide();
    });

    bullet.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        goToSlide(index);
        restartAutoSlide();
      }
    });
  });

  updateBullets();

  /* ScrollTrigger.create({
    trigger: root,
    start: "top bottom",
    end: () => "bottom top",
    onEnter: startAutoSlide,
    onEnterBack: startAutoSlide,
    onLeave: stopAutoSlide,
    onLeaveBack: stopAutoSlide,
  }); */
};

//lower movie
const initLowerRecipeMovie = () => {
  const movieButtons = document.querySelectorAll(".js-lower-recipe-movie-button");

  if (!movieButtons.length) return;

  const modal = document.createElement("div");
  modal.className = "p-lower__recipe__movie-modal js-lower-recipe-movie-modal";
  modal.setAttribute("aria-hidden", "true");

  modal.innerHTML = `
    <div class="p-lower__recipe__movie-modal__overlay js-lower-recipe-movie-modal-close"></div>
    <div class="p-lower__recipe__movie-modal__content" role="dialog" aria-modal="true" aria-label="作り方動画">
      <button type="button" class="p-lower__recipe__movie-modal__close js-lower-recipe-movie-modal-close" aria-label="動画を閉じる">
        <span></span>
        <span></span>
      </button>
      <div class="p-lower__recipe__movie-modal__body">
        <iframe
          class="js-lower-recipe-movie-iframe"
          src=""
          title="作り方動画"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        ></iframe>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  const iframe = modal.querySelector(".js-lower-recipe-movie-iframe");
  const closeButtons = modal.querySelectorAll(".js-lower-recipe-movie-modal-close");

  let lastFocusedButton = null;

  const openModal = (button) => {
    const youtubeId = button.dataset.youtubeId;

    if (!youtubeId) return;

    lastFocusedButton = button;

    iframe.src = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&mute=1&rel=0&playsinline=1`;
    iframe.title = button.getAttribute("aria-label") || "作り方動画";

    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("is-lower-recipe-movie-modal-open");
  };

  const closeModal = () => {
    modal.classList.remove("is-open");
    document.body.classList.remove("is-lower-recipe-movie-modal-open");

    // 閉じるボタンなど、モーダル内にフォーカスが残っていたら先に戻す
    if (lastFocusedButton) {
      lastFocusedButton.focus();
    } else if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    modal.setAttribute("aria-hidden", "true");

    // iframeのsrcを空にして、閉じたら動画を止める
    iframe.src = "";
  };

  movieButtons.forEach((button) => {
    button.addEventListener("click", () => {
      openModal(button);
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener("click", closeModal);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (!modal.classList.contains("is-open")) return;

    closeModal();
  });
};

/* Page ================ */
const initPageShown = () => {
  document.body.classList.add("is-shown");
};

/* Init ================ */
const init = async () => {
  initTopKVSlider();

  initLowerKVSwiper();
  mqSp.addEventListener("change", initLowerKVSwiper);

  await initLowerKVPhotoSwipe();

  initTopCatchphraseAnimation();
  initTopLineupItemAnimation();
  initTopLineupNorenAnimation();
  initTopGalleryTitleAnimation();
  initTopGalleryMovieSlider();
  initLowerRecipeMovie();
  
  initPageShown();
};

init();


//lower info link
const recipeLink = document.querySelector(".js-info-link");
if (recipeLink) {
  gsap.fromTo(
    recipeLink,
    {
      x: 220,
      rotation: 180,
      opacity: 0,
    },
    {
      x: 0,
      rotation: 0,
      opacity: 1,
      duration: 1.1,
      ease: "back.out(1.4)",
      delay: 0.4,
    },
  );
}


//lower recipe title
const recipeTitle = document.querySelector(".js-recipe-title");
if (recipeTitle) {
  const letters = recipeTitle.querySelectorAll(".js-recipe-title-letter");
  const shamoji = recipeTitle.querySelector(".js-recipe-title-shamoji");
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: recipeTitle,
      start: "top 80%",
      once: true,
    },
  });

  tl.fromTo(
    letters,
    {
      opacity: 0,
      y: 8,
      scale: 0.92,
    },
    {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.8,
      ease: "back.out(1.8)",
      stagger: 0.1,
      transformOrigin: "center center",
    },
  ).fromTo(
    shamoji,
    {
      opacity: 0,
      y: 6,
      rotation: -18,
    },
    {
      opacity: 1,
      y: 0,
      rotation: 0,
      duration: 0.2,
      ease: "back.out(1.8)",
      transformOrigin: "bottom center",
    },
    "+=0.2",
  ).to(shamoji, {
    rotation: 10,
    delay: 0.2,
    duration: 0.2,
    ease: "sine.inOut",
    yoyo: true,
    repeat: 3,
    transformOrigin: "bottom center",
  });
}

